import type { Letter } from '../../game/models/Letter';
import { BALL_RACK_LAYOUT, RACK_CENTER } from '../config/BallRackLayout';
import { SimplePoolPhysics, type CollisionCallback } from './SimplePoolPhysics';
import type { Ball } from './SimplePoolPhysics';

export interface LetterBallData {
  id: string;
  letter: Letter;
  ballNumber: number;
}

/**
 * PoolSimulation manages the custom physics world for pool table
 */
export class PoolSimulation {
  private physics: SimplePoolPhysics;
  private letterBallIds: Map<string, string> = new Map(); // game ID -> physics ID

  constructor() {
    this.physics = new SimplePoolPhysics();
    // Walls are created in SimplePoolPhysics constructor
  }

  /**
   * Set collision callback to track ball-ball collisions
   */
  setCollisionCallback(callback: CollisionCallback): void {
    this.physics.setCollisionCallback(callback);
  }

  /**
   * Create cue ball (white ball with no letter)
   */
  createCueBall(startPosition: { x: number; y: number }): Ball {
    return this.physics.createCueBall(startPosition.x, startPosition.y);
  }

  /**
   * Create letter balls in triangular rack formation
   */
  createLetterBalls(letterBallData: LetterBallData[]): Ball[] {
    const balls: Ball[] = [];
    this.letterBallIds.clear();

    for (const data of letterBallData) {
      const layout = BALL_RACK_LAYOUT.find(b => b.ballNumber === data.ballNumber);
      if (!layout) continue;

      const ballX = RACK_CENTER.x + layout.position.x;
      const ballY = RACK_CENTER.y + layout.position.y;
      
      const ball = this.physics.createLetterBall(data.id, ballX, ballY, data.letter.character);
      this.letterBallIds.set(data.id, ball.id);
      balls.push(ball);
    }

    return balls;
  }

  /**
   * Create letter balls at specific positions (for continuing a rack)
   */
  createLetterBallsAtPositions(letterBallData: LetterBallData[], positions: Map<string, { x: number; y: number }>): Ball[] {
    const balls: Ball[] = [];
    this.letterBallIds.clear();

    for (const data of letterBallData) {
      const pos = positions.get(data.id);
      if (!pos) continue;

      const ball = this.physics.createLetterBall(data.id, pos.x, pos.y, data.letter.character);
      this.letterBallIds.set(data.id, ball.id);
      balls.push(ball);
    }

    return balls;
  }

  /**
   * Launch cue ball with velocity and optional spin
   */
  launchCueBall(velocity: { x: number; y: number }, _angularVelocity: number = 0): void {
    this.physics.launchCueBall(velocity.x, velocity.y);
    
    // Log shot start
    const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
    console.log('🎱 SHOT STARTED:', {
      initialSpeed: speed.toFixed(3),
    });
  }

  /**
   * Update physics simulation
   */
  update(deltaTime: number): void {
    // Convert from milliseconds to seconds if needed
    const dt = deltaTime > 1 ? deltaTime / 1000 : deltaTime;
    this.physics.update(dt);
  }

  /**
   * Get cue ball reference
   */
  getCueBall(): Ball | null {
    return this.physics.getCueBall() || null;
  }

  /**
   * Set cue ball position (for repositioning between shots)
   */
  setCueBallPosition(position: { x: number; y: number }): void {
    const cueBall = this.physics.getCueBall();
    if (cueBall) {
      cueBall.x = position.x;
      cueBall.y = position.y;
      cueBall.vx = 0;
      cueBall.vy = 0;
    }
  }

  /**
   * Get letter ball by ID
   */
  getLetterBall(ballId: string): Ball | undefined {
    const physicsId = this.letterBallIds.get(ballId);
    if (!physicsId) return undefined;
    
    const balls = this.physics.getBalls();
    return balls.find(b => b.id === physicsId);
  }

  /**
   * Get all letter balls
   */
  getAllLetterBalls(): Ball[] {
    const balls = this.physics.getBalls();
    // Return all balls except cue ball
    return balls.filter(b => !b.isCueBall);
  }

  /**
   * Remove letter ball from simulation (after pocketing)
   */
  removeLetterBall(ballId: string): void {
    const physicsId = this.letterBallIds.get(ballId);
    if (physicsId) {
      this.physics.removeBall(physicsId);
      this.letterBallIds.delete(ballId);
    }
  }

  /**
   * Check if all balls have stopped moving
   */
  areAllBallsStopped(_threshold: number = 0.05): boolean {
    // Note: SimplePoolPhysics uses fixed threshold internally
    return this.physics.areAllBallsStopped();
  }

  /**
   * Get all balls (for rendering)
   */
  getBalls(): Ball[] {
    return this.physics.getBalls();
  }

  /**
   * Reset simulation
   */
  reset(): void {
    this.letterBallIds.clear();
    // Note: SimplePoolPhysics will need a reset method
    // For now, we'll just clear our tracking
  }
  
  /**
   * Clear all balls from physics engine
   */
  clearAllBalls(): void {
    this.physics.clearAllBalls();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.reset();
  }
}
